// routes/routes.tsx
import React from "react";
import { createBrowserRouter, RouteObject } from "react-router-dom";

import Page404 from "pages/page-404";
import PageHome from "pages/page-home";
import PageCareer from "pages/page-career";
import PageArticle from "pages/page-article";
import PageOurStory from "pages/page-our-story";
import PageAllArticles from "pages/page-all-articles";
import PageAllProducts from "pages/page-all-products";
import PageProjectShowCase from "pages/page-project-show-case";
import PageConnectWithUs from "pages/page-connect-with-us";
import PageProducts from "pages/page-products";
import PageProductsWithPricing from "pages/page-products-with-pricing";

import pathnames from "routes/pathnames";

import ScrollToTop from "common/scroll-to-top";
import LangLayout from "layouts/lang-layout";
import RootRedirect from "components/app-root-redirect";

// ---------------------------
// Base route config
// ---------------------------
type BaseRoute = { path: string; element: React.ComponentType<any> };

const baseRoutes: BaseRoute[] = [
  { path: pathnames.PageHome, element: PageHome },
  { path: pathnames.PageAllArticles, element: PageAllArticles },
  { path: `${pathnames.PageProjectShowCase}/:id`, element: PageProjectShowCase },
  { path: pathnames.PageConnectWithUs, element: PageConnectWithUs },
  { path: `${pathnames.PageArticle}/:id`, element: PageArticle },
  { path: pathnames.PageCareer, element: PageCareer },
  { path: pathnames.PageAllProducts, element: PageAllProducts },
  { path: pathnames.PageOurStory, element: PageOurStory },
  { path: pathnames.PageProducts, element: PageProducts },
  { path: pathnames.PageProductsWithPricing, element: PageProductsWithPricing },
];

// Helper to wrap element with ScrollToTop
const wrap = (Comp: React.ComponentType<any>) => (
  <ScrollToTop>
    <Comp />
  </ScrollToTop>
);

// Turn a "/path" into a child path for nested routes
const asChildPath = (p: string) => {
  if (p === "/") return ""; // becomes index route
  return p.replace(/^\//, "");
};

// ---------------------------
// Build language-prefixed routes
// ---------------------------
const langChildren: RouteObject[] = [
  // "/:lang" index
  ...baseRoutes
    .filter(r => r.path === "/")
    .map(({ element }) => ({ index: true, element: wrap(element) })),
  // "/:lang/xxx"
  ...baseRoutes
    .filter(r => r.path !== "/")
    .map(({ path, element }) => ({ path: asChildPath(path), element: wrap(element) })),

  // 404 for unknown paths within /:lang
  { path: "*", element: wrap(Page404) },
];

// ---------------------------
// Final router
// ---------------------------
export const router = createBrowserRouter([
  // Root index "/" → redirect based on current language from Redux
  { 
    path: "/", 
    element: <RootRedirect />
  },

  // Language-prefixed group (/:lang/*)
  { 
    path: "/:lang", 
    element: <LangLayout />, 
    children: langChildren 
  },

  // Catch-all 404
  { 
    path: "*", 
    element: wrap(Page404) 
  },
]);